import { AssetTreePicker, ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Box, Typography } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import Button from "@mui/material/Button";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHandleTables } from "./components/hooks/useHandleTables";
import { useInitialValues } from "./components/hooks/useInitialValues";
import { useSearchParamsToReducer } from "./components/hooks/useSearchParamsToReducer";
import SplitScreen from "./components/SplitScreen";
import { useSearchStatistics } from "./components/statistics/hooks/useSearchStatistics";
import { useAssetDetailsContext } from "./context/assetDetailsContext";
import { useInitializeAssetDetails } from "./hooks/useInitializeAssetDetails";
import "./styles.css";

export const AssetDetails = () => {
	const isMountRef = useRef(true);
	const navigate = useNavigate();
	const {
		state: {
			assetSearchId,
			assetSearchName,
			statisticsTable,
			workOrdersTable,
			assetsTable,
			currentGridName,
			assetFullPath,
		},
	} = useAssetDetailsContext();
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");
	const [isStatisticsLoading, setIsStatisticsLoading] = useState(false);
	const [statisticsError, setStatisticsError] = useState("");

	const getCompactPath = (fullPath: string) => {
		if (!fullPath) return "Loading path...";
		const parts = fullPath.split("/");
		if (parts.length <= 3) return fullPath;
		return `${parts[0]}/.../${parts[parts.length - 2]}/${
			parts[parts.length - 1]
		}`;
	};

	const changeAsset = (value: number | null) => {
		navigate(
			`/asset-details/${value}?resource=${currentGridName}&assets=true&statistics=true&workOrders=true`,
			{ replace: true },
		);
	};

	const searchStatistics = useSearchStatistics({
		setIsStatisticsLoading,
		setStatisticsError,
	});

	useInitialValues();
	useSearchParamsToReducer();
	useInitializeAssetDetails({ setIsLoading, setError });

	const [handleAssetsTable, handleStatisticsTable, handleWorkOrdersTable] =
		useHandleTables();

	useEffect(() => {
		// Only search if we have a valid assetSearchId (not 0, undefined, or null)
		// and it's not the first mount
		if (!isMountRef.current && assetSearchId && assetSearchId !== 0) {
			searchStatistics();
		}
		isMountRef.current = false;
	}, [assetSearchId]);

	return (
		<>
			<HelmetDexteel title="Asset Details" />
			<Box sx={{ height: "auto", padding: 2 }}>
				<Grid2
					container
					sx={{ marginBottom: 1, minHeight: "60px" }}
					alignItems="center"
					spacing={2}
				>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<Box>
							<Grid2 size={{ xs: 12 }}>
								<Typography
									variant="caption"
									sx={{
										margin: 0,
										fontWeight: 400,
										color: "text.secondary",
										fontSize: "0.7rem",
										lineHeight: 1.1,
										opacity: 0.8,
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
										textAlign: { xs: "center", md: "left" },
									}}
								>
									{getCompactPath(assetFullPath as string)}
								</Typography>
							</Grid2>
							<Typography
								variant="h6"
								sx={{
									margin: "-8px 0 0px",
									fontWeight: 600,
									userSelect: "none",
									textTransform: "uppercase",
									fontSize: { xs: "1.1rem", md: "1.4rem" },
									textAlign: { xs: "center", md: "left" },
								}}
								title={assetFullPath || "Loading path..."}
							>
								{assetSearchName}
							</Typography>
						</Box>
					</Grid2>

					<Grid2
						sx={{
							marginTop: { xs: 1, md: 0 },
						}}
						size={{ xs: 12, md: 4 }}
					>
						<AssetTreePicker
							label="Asset"
							value={Number.parseInt(assetSearchId as string) || 0}
							onChange={changeAsset}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 4 }}>
						<Box
							sx={{
								marginTop: { xs: 1, md: 0 },
								display: "flex",
								justifyContent: { xs: "center", md: "flex-end" },
							}}
						>
							<Box
								sx={{
									width: { xs: "100%", md: 250 },
									maxWidth: { xs: 300, md: "none" },
								}}
							>
								<Grid2 container>
									<Grid2 onClick={handleAssetsTable} size={{ xs: 6 }}>
										<Button
											fullWidth
											variant="contained"
											color={
												assetsTable && statisticsTable && workOrdersTable
													? "inherit"
													: assetsTable
														? "primary"
														: "inherit"
											}
											sx={{
												padding: "3px 16px",
												borderRadius: 0,
												borderTopLeftRadius: 3,
												textDecoration: "none",
												margin: 0,
												fontSize: "0.575rem",
											}}
										>
											RESOURCES
										</Button>
									</Grid2>
									<Grid2 onClick={handleStatisticsTable} size={{ xs: 6 }}>
										<Button
											fullWidth
											variant="contained"
											color={
												assetsTable && statisticsTable && workOrdersTable
													? "inherit"
													: statisticsTable
														? "primary"
														: "inherit"
											}
											sx={{
												padding: "3px 16px",
												borderRadius: 0,
												borderTopRightRadius: 3,
												textDecoration: "none",
												margin: 0,
												fontSize: "0.575rem",
											}}
										>
											STATISTICS
										</Button>
									</Grid2>
									<Grid2 onClick={handleWorkOrdersTable} size={{ xs: 12 }}>
										<Button
											fullWidth
											variant="contained"
											color={
												assetsTable && statisticsTable && workOrdersTable
													? "inherit"
													: workOrdersTable
														? "primary"
														: "inherit"
											}
											sx={{
												padding: "3px 16px",
												borderRadius: 0,
												borderBottomRightRadius: 3,
												borderBottomLeftRadius: 3,
												textDecoration: "none",
												margin: 0,
												fontSize: "0.575rem",
											}}
										>
											WORK ORDERS
										</Button>
									</Grid2>
								</Grid2>
							</Box>
						</Box>
					</Grid2>
				</Grid2>

				<Box sx={{ height: "calc(100vh - 160px)" }}>
					<SplitScreen
						searchStatistics={searchStatistics}
						isStatisticsLoading={isStatisticsLoading}
					/>
				</Box>

				<ErrorModal error={error} onHide={() => setError("")} />
				<ErrorModal
					error={statisticsError}
					onHide={() => setStatisticsError("")}
				/>
			</Box>
		</>
	);
};
